import LedgerController from '@web/modules/hardware-wallet/controllers/LedgerController'

// Phase 4 (keystore isolation) — Ledger on mobile-web.
//
// `MainController` (and the `LedgerSigner` it routes signing through) runs
// inside a Web Worker. Ledger talks over WebHID, and `navigator.hid` does NOT
// exist in a Worker — so the real device SDK (`@ledgerhq/device-management-kit`
// + the WebHID transport) must live on the PAGE.
//
// This controller is the page half of the bridge. The worker holds a thin proxy
// (`ledgerWorkerProxy.ts`) whose method calls round-trip here over `postMessage`
// (see `WebViewWorker.web.tsx` `hwLedgerInvoke`). Only non-secret data crosses:
// derived public addresses, unsigned payloads and returned signatures. The
// decrypted keystore `#mainKey`/seed never leave the worker, and every device
// operation requires physical on-device confirmation, so running the device
// transport on the page does not widen the keystore-isolation attack surface.
//
// The extension does not need any of this: there the controller is constructed
// in the foreground tab, which already has `navigator.hid`.

class LedgerPageController {
  // The real, unmodified `@web` controller — identical to what the extension
  // uses. Everything below is transport, not behavior.
  #ctrl = new LedgerController()

  // Snapshot of the state the worker proxy mirrors synchronously (the signer
  // reads `isUnlocked()` / `walletSDK` inline, which cannot be async RPCs).
  #state() {
    return {
      unlockedPath: this.#ctrl.unlockedPath,
      unlockedPathKeyAddr: this.#ctrl.unlockedPathKeyAddr,
      deviceModel: this.#ctrl.deviceModel,
      deviceId: this.#ctrl.deviceId,
      // The SDK session is per-connection; the proxy needs to know whether one
      // is live, because `MainController.#handleAccountPickerInitLedger` and
      // `LedgerSigner.#prepareForSigning` both gate on `walletSDK` truthiness.
      hasWalletSDK: !!this.#ctrl.walletSDK
    }
  }

  // Returns the resulting unlocked state alongside the status so the worker
  // proxy can refresh its synchronous mirror.
  async unlock(path: string, expectedKeyOnThisPath?: string) {
    const status = await this.#ctrl.unlock(path, expectedKeyOnThisPath)

    return { status, ...this.#state() }
  }

  async retrieveAddresses(paths: string[]) {
    const addresses = await this.#ctrl.retrieveAddresses(paths)

    return { addresses, ...this.#state() }
  }

  signPersonalMessage(derivationPath: string, messageHex: string) {
    return this.#ctrl.signPersonalMessage(derivationPath, messageHex)
  }

  signTransaction(derivationPath: string, transaction: Uint8Array) {
    return this.#ctrl.signTransaction(derivationPath, transaction)
  }

  signTypedData(params: Parameters<LedgerController['signTypedData']>[0]) {
    return this.#ctrl.signTypedData(params)
  }

  async signingCleanup() {
    await this.#ctrl.signingCleanup()
  }

  // Closes the live SDK session. `MainController` calls this before starting a
  // new one, otherwise the device throws "invalid channel" when the user has
  // used it with another app in between.
  async cleanUp() {
    await this.#ctrl.cleanUp()

    return this.#state()
  }
}

export default LedgerPageController
